import { Injectable } from "@angular/core";
import { GlobalConstants } from "../../constants";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import * as moment from "moment";
import {
  GeneralLedgerDetailAdapter,
  GeneralLedgerDetail,
} from "../Model/gl-detail";

@Injectable({
  providedIn: "root",
})
export class GeneralLedgerDetailService {
  private baseURL =
    GlobalConstants.BASE_API_URL + ":" + GlobalConstants.BASE_API_URL_PORT;
  private API_NAME = "glDetail";

  constructor(
    private httpClient: HttpClient,
    private adapter: GeneralLedgerDetailAdapter
  ) {}

  getAll(documentNumber): Observable<GeneralLedgerDetail[]> {
    const url = `${this.baseURL}/${this.API_NAME}/api/get/all`;
    const params = new HttpParams().set("documentNumber", documentNumber);
    return this.httpClient.get(url, { params }).pipe(
      // Adapt each item in the raw data array
      map((data: any[]) => data["data"].map((item) => this.adapter.adapt(item)))
    );
  }

  getByBatch(batchNumber): Observable<GeneralLedgerDetail[]> {
    const url = `${this.baseURL}/${this.API_NAME}/api/get/allByBatch`;
    const params = new HttpParams().set("batchNumber", batchNumber);
    return this.httpClient.get(url, { params }).pipe(
      // Adapt each item in the raw data array
      map((data: any[]) => data["data"].map((item) => this.adapter.adapt(item)))
    );
  }

  getByDocument(documentNumber): Observable<GeneralLedgerDetail[]> {
    const url = `${this.baseURL}/${this.API_NAME}/api/get/allByDocument`;
    const params = new HttpParams().set("documentNumber", documentNumber);
    return this.httpClient.get(url, { params }).pipe(
      // Adapt each item in the raw data array
      map((data: any[]) => data["data"].map((item) => this.adapter.adapt(item)))
    );
  }

  getForFiscalYear(fiscalYear, chartId): Observable<GeneralLedgerDetail[]> {
    const url = `${this.baseURL}/${this.API_NAME}/api/get/allPostedByYear`;
    const params = new HttpParams()
      .set("fiscalYear", fiscalYear)
      .set("chartRowId", chartId);
    return this.httpClient.get(url, { params }).pipe(
      // Adapt each item in the raw data array
      map((data: any[]) => data["data"].map((item) => this.adapter.adapt(item)))
    );
  }

  getAllFiscalYear(fiscalYear): Observable<GeneralLedgerDetail[]> {
    const url = `${this.baseURL}/${this.API_NAME}/api/get/allPosted`;
    const params = new HttpParams().set("fiscalYear", fiscalYear);
    return this.httpClient.get(url, { params }).pipe(
      // Adapt each item in the raw data array
      map((data: any[]) => data["data"].map((item) => this.adapter.adapt(item)))
    );
  }

  getByDate(startDate, endDate): Observable<GeneralLedgerDetail[]> {
    const url = `${this.baseURL}/${this.API_NAME}/api/get/by/date`;
    const params = new HttpParams()
      .set("startDate", startDate)
      .set("endDate", endDate);
    return this.httpClient.get(url, { params }).pipe(
      // Adapt each item in the raw data array
      map((data: any[]) => data["data"].map((item) => this.adapter.adapt(item)))
    );
  }

  checkForDuplicate(
    busUnitRowID,
    object,
    subsidiary,
    id
  ): Observable<GeneralLedgerDetail[]> {
    // If id is <> null, then it is an update and need to make sure
    // not checking for a duplicate on it's on record ID, else
    // check without ID on a create
    if (id != null) {
      const url = `${this.baseURL}/${this.API_NAME}/api/check/duplicate`;
      const params = new HttpParams()
        .set("busUnitRowID", busUnitRowID)
        .set("object", object)
        .set("subsidiary", subsidiary)
        .set("id", id);
      return this.httpClient.get(url, { params }).pipe(
        // Adapt each item in the raw data array
        map((data: any[]) =>
          data["data"].map((item) => this.adapter.adapt(item))
        )
      );
    } else {
      // Creation duplicate check, since no ID.  No need to check by ID
      const url = `${this.baseURL}/${this.API_NAME}/api/get/byBusObjSub`;
      const params = new HttpParams()
        .set("busUnitRowID", busUnitRowID)
        .set("object", object)
        .set("subsidiary", subsidiary);
      return this.httpClient.get(url, { params }).pipe(
        // Adapt each item in the raw data array
        map((data: any[]) =>
          data["data"].map((item) => this.adapter.adapt(item))
        )
      );
    }
  }

  update(formData, id) {
    // Format lastUpdated to IBM i standards
    formData.lastUpdated = moment().format("YYYY-MM-DD-HH.mm.ss");

    // Add database ID to the JSON object for update service
    formData.ID = id;
    formData.changeID = localStorage.getItem("user");
    const url = `${this.baseURL}/${this.API_NAME}/api/update`;
    return this.httpClient.post(url, formData);
  }

  updateFromCustomerLedger(formData) {
    // Format lastUpdated to IBM i standards
    formData.lastUpdated = moment().format("YYYY-MM-DD-HH.mm.ss");

    // Add database ID to the JSON object for update service
    formData.changeID = localStorage.getItem("user");

    const url = `${this.baseURL}/${this.API_NAME}/api/updateFromCustomerLedger`;
    return this.httpClient.post(url, formData);
  }

  updateFromVendorLedger(formData) {
    // Format lastUpdated to IBM i standards
    formData.lastUpdated = moment().format("YYYY-MM-DD-HH.mm.ss");

    // Add database ID to the JSON object for update service
    formData.changeID = localStorage.getItem("user");

    const url = `${this.baseURL}/${this.API_NAME}/api/updateFromVendorLedger`;
    return this.httpClient.post(url, formData);
  }

  updateToPosted(formData) {
    // Format lastUpdated to IBM i standards
    formData.lastUpdated = moment().format("YYYY-MM-DD-HH.mm.ss");

    // Add database ID to the JSON object for update service
    formData.changeID = localStorage.getItem("user");

    const url = `${this.baseURL}/${this.API_NAME}/api/updateToPosted`;
    return this.httpClient.post(url, formData);
  }

  delete(id) {
    const url = `${this.baseURL}/${this.API_NAME}/api/delete`;
    return this.httpClient.post(url, { id: id });
  }

  deleteByDocumentNumber(documentNumber) {
    const url = `${this.baseURL}/${this.API_NAME}/api/deleteByDocument`;
    return this.httpClient.post(url, { documentNumber: documentNumber });
  }

  deleteByBatchNumber(batchNumber) {
    const url = `${this.baseURL}/${this.API_NAME}/api/deleteByBatch`;
    return this.httpClient.post(url, { batchNumber: batchNumber });
  }

  create(formData) {
    formData.changeID = localStorage.getItem("user");

    if (formData.glDate) {
      formData.invoiceDate = formData.glDate.toISOString().substring(0, 10);
      formData.taxDate = formData.glDate.toISOString().substring(0, 10);
      formData.glDate = formData.glDate.toISOString().substring(0, 10);
    }

    const url = `${this.baseURL}/${this.API_NAME}/api/create`;
    return this.httpClient.post(url, formData);
  }
}
